import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ProductFormBasic from './ProductFormBasic';
import ProductFormBarcodes from './ProductFormBarcodes';
import ProductFormPricing from './ProductFormPricing';
import ProductFormStock from './ProductFormStock';
import ProductImageGallery from './ProductImageGallery';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductForm = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  editingProduct,
  brands,
  subBrands,
  categories,
  menuOptions,
  subMenuOptions,
  onBrandChange,
  onMenuChange,
  // Multi-image gallery state (Feb 2026 multi-image upgrade)
  gallery,
  setGallery,
  pendingImageFiles,
  setPendingImageFiles,
}) => {
  // Margin-engine state — auto-fills Outward Selling Price for NEW products
  // when Unit=PCS. Locks once the user manually types into the field; resets
  // when the user clears the field (back to auto-calc on next MRP/Inward change).
  const isNewProduct = !editingProduct;
  const [outwardLocked, setOutwardLocked] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null); // { applied, our_margin_pct, message }
  const calcAbortRef = useRef(null);
  const calcTimerRef = useRef(null);
  // Edit mode: remember the pricing loaded from the product so we only
  // auto-recompute Outward when the USER changes MRP / Inward (never on open).
  const initialPricingRef = useRef(null);
  useEffect(() => {
    if (initialPricingRef.current === null) {
      initialPricingRef.current = {
        inward: (formData.inward_price_incl_gst ?? '').toString(),
        mrp: (formData.mrp ?? '').toString(),
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateExclGst = (inclPrice, gstPct) => {
    if (!inclPrice || !gstPct) return '';
    const rate = parseFloat(gstPct) / 100;
    return (parseFloat(inclPrice) / (1 + rate)).toFixed(2);
  };

  const handleInwardPriceChange = (value) => {
    setFormData({
      ...formData,
      inward_price_incl_gst: value,
      inward_price_excl_gst: calculateExclGst(value, formData.gst_percentage)
    });
  };

  const handleGstChange = (value) => {
    setFormData({
      ...formData,
      gst_percentage: value,
      inward_price_excl_gst: calculateExclGst(formData.inward_price_incl_gst, value)
    });
  };

  const setBarcodes = (newBarcodes) => {
    setFormData({ ...formData, barcodes: newBarcodes });
  };

  // Manual override hook: invoked by the Outward Selling Price input.
  // Sets `outwardLocked=true` when the user types a value; resets to false
  // when they clear the field so auto-calc resumes on the next MRP/Inward change.
  const handleOutwardChange = (value) => {
    const trimmed = (value || '').toString().trim();
    setOutwardLocked(trimmed.length > 0);
    if (trimmed.length > 0) setAutoStatus(null);
    setFormData({ ...formData, outward_selling_price: value });
  };

  // Auto-pricing effect: on MRP / Inward / Unit change, if unit=PCS, both
  // prices > 0, and field is not manually locked — call
  // /margin-master/calculate (debounced 350ms) and fill the field.
  // NEW products: runs as soon as inputs are valid.
  // EDIT mode (Oct 2026 fix): runs ONLY after the user changes MRP or the
  // Inward (Incl. GST) price — opening the dialog never overwrites the
  // stored Outward Selling Price.
  useEffect(() => {
    if (!isNewProduct) {
      const init = initialPricingRef.current;
      const unchanged = init &&
        (formData.inward_price_incl_gst ?? '').toString() === init.inward &&
        (formData.mrp ?? '').toString() === init.mrp;
      if (unchanged) return;
    }
    if (outwardLocked) return;

    const unit = (formData.measurement_unit || '').toString().toUpperCase();
    const mrp = parseFloat(formData.mrp);
    const inward = parseFloat(formData.inward_price_incl_gst);
    const category = (formData.category || '').toString();
    if (unit !== 'PCS' || !mrp || !inward || mrp <= 0 || inward <= 0) {
      // Reset status banner when preconditions stop being met
      if (autoStatus) setAutoStatus(null);
      return;
    }
    // Category exclusion (Oils / Edible Oils / Cooking Oil ...): auto-pricing
    // is disabled. Surface a small status banner so the operator knows the
    // outward field is intentionally unmanaged for this category.
    if (category.toLowerCase().indexOf('oil') !== -1) {
      setAutoStatus({ applied: false, reason: 'excluded_category', category });
      return;
    }

    // Debounce + cancel any in-flight request
    clearTimeout(calcTimerRef.current);
    calcTimerRef.current = setTimeout(async () => {
      try {
        if (calcAbortRef.current) calcAbortRef.current.abort();
        const ctrl = new AbortController();
        calcAbortRef.current = ctrl;
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/margin-master/calculate`,
          { mrp, inward_price_incl_gst: inward, unit: 'PCS', category: formData.category || null },
          { headers: { Authorization: `Bearer ${token}` }, signal: ctrl.signal }
        );
        const d = res.data || {};
        if (d.applied) {
          setFormData((prev) => ({
            ...prev,
            outward_selling_price: d.outward_selling_price.toString(),
          }));
          setAutoStatus({ applied: true, our_margin_pct: d.our_margin_pct });
        } else {
          // Per spec: don't auto-fill on no_match / non_positive — toast a warning
          // and leave the field for manual entry.
          setAutoStatus({ applied: false, message: d.message || 'Auto-pricing skipped.' });
          if (d.reason && d.reason !== 'unit_not_pcs' && d.reason !== 'invalid_inputs') {
            toast.warning(d.message || 'Could not auto-fill Outward Selling Price — enter manually.');
          }
          // New products: clear any previously-auto-filled value so the user
          // enters fresh. Edit mode: KEEP the stored value (never destroy it).
          if (isNewProduct) {
            setFormData((prev) => ({ ...prev, outward_selling_price: '' }));
          }
        }
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          // Surface but don't block product creation
          console.warn('margin-master calc failed', err);
        }
      }
    }, 350);

    return () => clearTimeout(calcTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isNewProduct,
    outwardLocked,
    formData.mrp,
    formData.inward_price_incl_gst,
    formData.measurement_unit,
    formData.category,
  ]);

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <ProductImageGallery
        productId={editingProduct?.id || null}
        gallery={gallery || []}
        setGallery={setGallery}
        pendingFiles={pendingImageFiles || []}
        setPendingFiles={setPendingImageFiles}
        setFormData={setFormData}
      />

      <div className="grid grid-cols-2 gap-4">
        <ProductFormBasic
          formData={formData}
          setFormData={setFormData}
          brands={brands}
          subBrands={subBrands}
          categories={categories}
          menuOptions={menuOptions}
          subMenuOptions={subMenuOptions}
          onBrandChange={onBrandChange}
          onMenuChange={onMenuChange}
        />

        <ProductFormBarcodes
          barcodes={formData.barcodes}
          setBarcodes={setBarcodes}
        />

        <ProductFormStock
          formData={formData}
          setFormData={setFormData}
        />

        <ProductFormPricing
          formData={formData}
          setFormData={setFormData}
          onInwardPriceChange={handleInwardPriceChange}
          onGstChange={handleGstChange}
          onOutwardChange={handleOutwardChange}
          autoStatus={autoStatus}
          outwardLocked={outwardLocked}
          isNewProduct={isNewProduct}
        />
      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="product-form-cancel-btn">Cancel</Button>
        <Button type="submit" data-testid="product-form-submit-btn">{editingProduct ? 'Update' : 'Create'} Product</Button>
      </div>
    </form>
  );
};

export default ProductForm;
